import type { PlainObject } from '../../types';
import type { Element } from '@xmpp/xml';
import type { Options } from '@xmpp/connection';
import type { Reconnect } from '@xmpp/reconnect';
import type { Middleware } from '@xmpp/middleware';
import type { StreamFeatures } from '@xmpp/stream-features';
import type { IQCaller } from '@xmpp/iq/caller.js';
import type { IQCallee } from '@xmpp/iq/callee.js';
import type { SASL2 } from '@xmpp/sasl2';
import type { SASL } from '@xmpp/sasl';
import type { Resource } from '@xmpp/resource-binding';
import type { Bind2 } from '@xmpp/client-core/src/bind2/bind2.js';
import type { Token, FAST } from '@xmpp/client-core/src/fast/fast.js';

import { jid, xml, Client } from '@xmpp/client-core';
import _reconnect from '@xmpp/reconnect';
import _websocket from '@xmpp/websocket';
import _middleware from '@xmpp/middleware';
import _streamFeatures from '@xmpp/stream-features';
import _iqCaller from '@xmpp/iq/caller.js';
import _iqCallee from '@xmpp/iq/callee.js';
import _sasl2 from '@xmpp/sasl2';
import _sasl from '@xmpp/sasl';
import _resourceBinding from '@xmpp/resource-binding';
import _bind2 from '@xmpp/client-core/src/bind2/bind2.js';
import _fast from '@xmpp/client-core/src/fast/fast.js';
import SASLFactory from 'saslmechanisms';
import plain from '@xmpp/sasl-plain';
import anonymous from '@xmpp/sasl-anonymous';
import htsha256none from '@xmpp/sasl-ht-sha-256-none';
import { createOnAuthenticate, getDomain } from './xmpp.utils';
import ChatUtils from './ChatUtils';
import Utils from '../../Utils';

export type XMPPResource = Resource | string | (() => Promise<string>) | undefined;
export type XMPPCredentialsProp = string | undefined;
export type XMPPCredentialsAuth = (
  credentials: Partial<XMPPCredentialsObj>,
  mechanism: string,
  userAgent?: XMPPUserAgent
) => Promise<void>;
export type XMPPCredentials =
  | Partial<XMPPCredentialsObj>
  | ((callback: XMPPCredentialsAuth, mechanism: string) => Promise<void>)
  | undefined;
export type XMPPUserAgent = Element | undefined;

export interface XMPPCredentialsObj {
  username: XMPPCredentialsProp;
  password: XMPPCredentialsProp;
  token: Token;
}

export interface XMPPOptions extends Options, Partial<XMPPCredentialsObj> {
  resource?: XMPPResource;
  credentials?: XMPPCredentials;
  userAgent?: XMPPUserAgent;
}

export interface XMPPClient extends Client {
  entity: Client;
  reconnect: Reconnect<Client>;
  websocket: ReturnType<typeof _websocket>;
  middleware: Middleware<Client>;
  streamFeatures: StreamFeatures<Client>;
  iqCaller: IQCaller<Client>;
  iqCallee: IQCallee<Client>;
  saslFactory: SASLFactory;
  sasl2: SASL2;
  sasl: SASL;
  resourceBinding: ReturnType<typeof _resourceBinding>;
  mechanisms: PlainObject[];
  bind2: Bind2;
  fast: FAST;
  options: XMPPOptions;
  sendOnline: (element: Element, ...args: unknown[]) => Promise<void>;
  tcpEnabled?: boolean;
  tlsEnabled?: boolean;
}

function xmppClient(options: XMPPOptions): XMPPClient {
  let { resource, credentials, username, password, userAgent, ...params } = options;

  const { domain, service } = params;

  if (!domain && service) {
    params.domain = getDomain(service);
  }

  const entity = new Client(params);

  if (username && params.domain) {
    entity.jid = jid(username, params.domain);
  }

  const reconnect = _reconnect({ entity });
  const websocket = _websocket({ entity });
  const middleware = _middleware({ entity });
  const streamFeatures = _streamFeatures({ middleware });
  const iqCaller = _iqCaller({ middleware, entity });
  const iqCallee = _iqCallee({ middleware, entity });

  // SASL mechanisms - order matters and define priority
  const saslFactory = new SASLFactory();
  const mechanisms = Object.entries({ plain, anonymous }).map(([k, v]) => ({ [k]: v(saslFactory) }));

  userAgent ??= xml('user-agent', { id: ChatUtils.getUniqueId() });

  // Stream features - order matters and define priority
  const sasl2 = _sasl2(
    { streamFeatures, saslFactory },
    createOnAuthenticate(credentials ?? { username, password }, userAgent)
  );
  const fast = _fast({ sasl2, entity });

  sasl2.setup({ fast });

  // SASL2 inline features
  const bind2 = _bind2({ sasl2, entity }, resource as string | (() => Promise<string>));

  // FAST mechanisms - order matters and define priority
  htsha256none(fast.saslFactory);

  // Stream features - order matters and define priority
  const sasl = _sasl(
    { streamFeatures, saslFactory },
    createOnAuthenticate(credentials ?? { username, password }, userAgent)
  );
  const resourceBinding = _resourceBinding({ iqCaller, streamFeatures }, resource as Resource | undefined);

  iqCallee?.get('urn:xmpp:ping', 'ping', () => ({}));

  const sendOnline = function (element: Element, ...args: unknown[]): Promise<void> {
    if (entity.status === 'online') {
      return entity.send.call(entity, element, ...args);
    } else {
      const message = "You are not connected to chat. Please call 'connect' function first";

      Utils.DLog('[Chat][Error]', message);

      return Promise.reject(new Error(message));
    }
  };

  return Object.assign(entity, {
    entity,
    reconnect,
    websocket,
    middleware,
    streamFeatures,
    iqCaller,
    iqCallee,
    saslFactory,
    sasl2,
    sasl,
    resourceBinding,
    mechanisms,
    bind2,
    fast,
    sendOnline,
  });
}

export { jid, xml, xmppClient };
